import { FormContainer, FormContainerProps } from 'src/UI/dynamicUI/layout/DeprecatedDynamicEntity';
import { Alert } from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { InlineClickableText } from 'src/common/components/inline-clickable-text/InlineClickableText';
import { useConnectAsset } from 'src/financial/financial-data/hooks/useConnectAsset';
import { useProviderReferenceSharing } from 'src/financial/financial-data/hooks/useProviderReferenceSharing';
import {
  describeProviderReferenceHolders,
  describeSharedReferenceResolution,
} from 'src/financial/financial-data/providerReferenceHolders';
import { useFlags } from 'src/feature-flags/FlagsHook';
import PageLoader from 'src/UI/loader/PageLoader';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useParams } from 'react-router';
import { FinancialItemFormHeader } from 'src/financial/financial-data/financial-item/rework/FinancialItemFormHeader';

type FinancialFormContainerProps = {
  feedInfo: FeedInfo;
  onConnect?: (id: string) => void;
  hasProviderReference?: boolean;
  /** Entity type, e.g. 'SpendingAccount'. Names what the reworked header's save button saves. */
  product?: string;
} & FormContainerProps;

export type FeedInfo = {
  isUpdatedAutomatically: boolean;
  hasFeedData: boolean;
};

export const FinancialFormContainer = (props: FinancialFormContainerProps) => {
  const { feedInfo, hasProviderReference = false, product, ...formContainerProps } = props;
  const { isUpdatedAutomatically, hasFeedData } = feedInfo;
  const { id: assetId = '', clientId = '' } = useParams<{ id: string; clientId: string }>();
  const { connectAsset, isLoading } = useConnectAsset({ clientId });
  const { flagEnabledFinancialItemOwners, flagEnabledFinancialRework } = useFlags();
  const { data: providerReferenceSharing } = useProviderReferenceSharing(
    clientId,
    hasProviderReference ? assetId : '',
  );

  const handleConnectAsset = async () => {
    try {
      await connectAsset(assetId);
      props.onConnect?.(assetId);
    } catch (error) {
      notifyAndLogError(
        'Failed to connect asset to feed',
        'Something went wrong when trying to connect the feed. Please try again and if the problem persists, contact support.',
        error,
      );
    }
  };

  const content = (
    <>
      {isLoading && <PageLoader />}
      {!isUpdatedAutomatically && hasFeedData && (
        <Alert variant="light" color="blue" radius="lg" icon={<IconInfoCircle />} mb={'xxs'}>
          We found a data feed that matches this provider reference number. To keep this asset
          updated automatically,{' '}
          <InlineClickableText onClick={handleConnectAsset}>
            connect it to the feed
          </InlineClickableText>
          .
        </Alert>
      )}
      {providerReferenceSharing?.needs_review && (
        <Alert
          variant="light"
          color="yellow"
          radius="lg"
          icon={<IconAlertTriangle />}
          mb={'xxs'}
          data-testid="provider-reference-sharing-banner"
        >
          This provider reference number is also used on an asset for{' '}
          {describeProviderReferenceHolders(providerReferenceSharing.holders)}.{' '}
          {describeSharedReferenceResolution({
            involvesPension: providerReferenceSharing.involves_pension,
            ownersEnabled: flagEnabledFinancialItemOwners,
          })}
        </Alert>
      )}
      {props.children}
    </>
  );

  if (flagEnabledFinancialRework && product) {
    return (
      <FinancialItemFormHeader product={product} title={formContainerProps.title}>
        {content}
      </FinancialItemFormHeader>
    );
  }

  return <FormContainer {...formContainerProps}>{content}</FormContainer>;
};
